import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usuarioService from '../services/UsuarioService';
import LoadingSpinner from './common/LoadingSpinner';
import brandLogo from '../assets/logos/logo_transparent_background.png';
import brandLogoLight from '../assets/logos/white_logo_transparent_background.png';
import './Login.css';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState({ newPassword: '', repeatPassword: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setErrorMessage('El token es inválido o no fue provisto.');
        setIsValidToken(false);
        setIsValidating(false);
        return;
      }

      try {
        setIsValidating(true);
        const response = await usuarioService.validatePasswordResetToken(token);
        if (response?.ok && response?.valido !== false) {
          setIsValidToken(true);
          setErrorMessage('');
        } else {
          setIsValidToken(false);
          setErrorMessage(response?.error || 'El token no es válido o expiró.');
        }
      } catch (error) {
        setIsValidToken(false);
        setErrorMessage(error.message || 'El token no es válido o expiró.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.newPassword || !form.repeatPassword) {
      setErrorMessage('Completa ambos campos para continuar.');
      return;
    }
    if (form.newPassword.length < 6) {
      setErrorMessage('El nuevo password debe tener al menos 6 caracteres.');
      return;
    }
    if (form.newPassword !== form.repeatPassword) {
      setErrorMessage('La confirmación no coincide con el password.');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      await usuarioService.confirmPasswordReset(token, form.newPassword, form.repeatPassword);
      setSuccess(true);
    } catch (error) {
      setErrorMessage(error.message || 'No pudimos actualizar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-container reset-password-container">
      <div className="login-background">
        <div className="geometric-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
        </div>
      </div>

      <div className="login-content">
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-text">
              <img src={brandLogoLight} alt="aluxIA" className="branding-logo" />
              <p className="subtitle">Recuperá el acceso a tu cuenta</p>
              <p className="subtitle-secondary">Te guiamos para restablecer tu contraseña de forma segura.</p>
            </div>
          </div>
        </div>

        <div className="login-form-section reset-password-section">
          <div className="form-container">
            <div className="form-logos-container">
              <img src={brandLogo} alt="aluxIA" className="form-logo brand-logo-form" />
            </div>

            <div className="form-header">
              <h2 className="form-title">Restablecer contraseña</h2>
              <p className="form-subtitle">Ingresá una nueva contraseña para continuar.</p>
            </div>

            {isValidating && (
              <div className="reset-status">
                <LoadingSpinner message="Validando token..." size="small" />
              </div>
            )}

            {!isValidating && !isValidToken && (
              <div className="reset-status reset-error">
                {errorMessage || 'El token no es válido o expiró.'}
              </div>
            )}

            {!isValidating && isValidToken && !success && (
              <form onSubmit={handleSubmit} className="login-form">
                <div className="input-group">
                  <label className="input-label" htmlFor="newPassword">Nuevo password</label>
                  <input
                    type="password"
                    id="newPassword"
                    className="form-input"
                    value={form.newPassword}
                    onChange={(e) => handleChange('newPassword', e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    disabled={saving}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label" htmlFor="repeatPassword">Repetir password</label>
                  <input
                    type="password"
                    id="repeatPassword"
                    className="form-input"
                    value={form.repeatPassword}
                    onChange={(e) => handleChange('repeatPassword', e.target.value)}
                    placeholder="Repite tu password"
                    disabled={saving}
                  />
                </div>

                {errorMessage && <div className="reset-status reset-error">{errorMessage}</div>}

                <button type="submit" className="login-button" disabled={saving}>
                  {saving ? 'Actualizando...' : 'Actualizar password'}
                </button>
              </form>
            )}

            {success && (
              <div className="reset-success">
                <h3>Contraseña actualizada</h3>
                <p>Ya podés iniciar sesión con tu nueva contraseña.</p>
                <button type="button" className="login-button" onClick={() => navigate('/login')}>
                  Ir al login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
